// src/pages/Admin.jsx
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { routesData } from '../data/routesData';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Admin() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [searchParams] = useSearchParams();

  // --- AUTH ---
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
    });
    return () => unsub();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      setLoginLoading(true);
      await signInWithEmailAndPassword(
        auth,
        loginForm.email.trim(),
        loginForm.password
      );
    } catch (err) {
      console.error('Erreur login :', err);
      setLoginError(t('admin_login_error'));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  // --- Recherche billet ---
  const [bookingRefSearch, setBookingRefSearch] = useState(
    searchParams.get('bookingRef') || ''
  );
  const [phoneSearch, setPhoneSearch] = useState(
    searchParams.get('phone') || ''
  );
  const [foundReservations, setFoundReservations] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [hasAutoSearched, setHasAutoSearched] = useState(false);

  // --- Stats bus ---
  const [selectedTripId, setSelectedTripId] = useState('');
  const [dateStats, setDateStats] = useState('');
  const [selectedBusStats, setSelectedBusStats] = useState('');
  const [selectedTourStats, setSelectedTourStats] = useState('');
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsReservations, setStatsReservations] = useState([]);
  const totalSeats = 55;

  const searchReservations = async (refVal, phoneVal) => {
    setSearchError('');
    setFoundReservations([]);

    const refTrim = (refVal || '').trim();
    const phoneTrim = (phoneVal || '').trim();

    if (!refTrim && !phoneTrim) {
      setSearchError(t('admin_no_ref_or_phone'));
      return;
    }

    try {
      setSearchLoading(true);
      const reservationsRef = collection(db, 'reservations');
      let q;

      if (refTrim) {
        q = query(reservationsRef, where('bookingRef', '==', refTrim));
      } else {
        q = query(reservationsRef, where('passenger.phone', '==', phoneTrim));
      }

      const snapshot = await getDocs(q);
      const results = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setFoundReservations(results);

      if (results.length === 0) {
        setSearchError(t('admin_no_results'));
      }
    } catch (err) {
      console.error('Erreur recherche billet :', err);
      setSearchError(t('admin_search_error'));
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchTicket = async () => {
    await searchReservations(bookingRefSearch, phoneSearch);
  };

  useEffect(() => {
    const refFromUrl = searchParams.get('bookingRef') || '';
    const phoneFromUrl = searchParams.get('phone') || '';

    if (
      user &&
      !hasAutoSearched &&
      (refFromUrl || phoneFromUrl)
    ) {
      setHasAutoSearched(true);
      setBookingRefSearch(refFromUrl);
      setPhoneSearch(phoneFromUrl);
      searchReservations(refFromUrl, phoneFromUrl);
    }
  }, [user, hasAutoSearched, searchParams]);

  const handleLoadStats = async () => {
    setStatsReservations([]);
    if (!selectedTripId || !dateStats || !selectedBusStats || !selectedTourStats) return;

    try {
      setStatsLoading(true);
      const reservationsRef = collection(db, 'reservations');
      const q = query(
        reservationsRef,
        where('tripId', '==', selectedTripId),
        where('travelDate', '==', dateStats),
        where('busNumber', '==', parseInt(selectedBusStats)),
        where('tour', '==', selectedTourStats)
      );
      const snapshot = await getDocs(q);
      const results = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setStatsReservations(results);
    } catch (err) {
      console.error('Erreur chargement stats :', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const updateReservationStatus = async (reservationId, newStatus) => {
    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        status: newStatus,
        validatedAt: new Date().toISOString(),
        validatedBy: user ? user.uid : null,
      });

      setFoundReservations((prev) =>
        prev.map((r) =>
          r.id === reservationId ? { ...r, status: newStatus } : r
        )
      );
      setStatsReservations((prev) =>
        prev.map((r) =>
          r.id === reservationId ? { ...r, status: newStatus } : r
        )
      );
    } catch (err) {
      console.error('Erreur mise à jour statut :', err);
      alert("Impossible de mettre à jour le statut, vérifiez les règles Firestore.");
    }
  };

  const selectedTrip = routesData.find((r) => r.id === selectedTripId);
  const reservedSeatsCount = statsReservations.reduce(
    (sum, r) => sum + (Array.isArray(r.seats) ? r.seats.length : 0),
    0
  );
  const remainingSeats = totalSeats - reservedSeatsCount;

  if (!user) {
    return (
      <div className={`${isRTL ? 'rtl' : ''} bg-neutral-50 min-h-screen flex items-center justify-center px-4`}>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-neutral-100">
          <h1 className="text-2xl font-bold mb-2 text-center">
            🔐 {t('admin_login_title')}
          </h1>
          <p className="text-sm text-gray-600 mb-6 text-center">
            {t('admin_login_subtitle')}
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1">
                {t('admin_email')}
              </label>
              <input
                type="email"
                className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
                value={loginForm.email}
                onChange={(e) =>
                  setLoginForm({ ...loginForm, email: e.target.value })
                }
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">
                {t('admin_password')}
              </label>
              <input
                type="password"
                className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
                value={loginForm.password}
                onChange={(e) =>
                  setLoginForm({ ...loginForm, password: e.target.value })
                }
                required
              />
            </div>

            {loginError && (
              <p className="text-sm text-red-600 mb-2">{loginError}</p>
            )}

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-primary-600 text-white font-semibold py-3 rounded-xl shadow hover:bg-primary-700 transition disabled:opacity-50"
            >
              {loginLoading ? t('admin_login_loading') : t('admin_login_button')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isRTL ? 'rtl' : ''} bg-neutral-50 min-h-screen py-10`}>
      <div className="container mx-auto px-4 max-w-6xl">
        <div className={`flex ${isRTL ? 'flex-row-reverse' : ''} justify-between items-center mb-6`}>
          <motion.h1
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-4xl font-display font-bold text-neutral-900"
          >
            🛠️ {t('admin_panel_title')}
          </motion.h1>
          <div className={`${isRTL ? 'text-left' : 'text-right'}`}>
            <p className="text-xs text-gray-600">
              {t('admin_logged_in_as')}
            </p>
            <p className="text-sm font-semibold">{user.email}</p>
            <button
              onClick={handleLogout}
              className="mt-2 text-xs text-red-600 underline"
            >
              {t('admin_logout')}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Bloc vérification billet */}
          <motion.section
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white p-6 rounded-2xl shadow-lg border border-neutral-100"
          >
            <h2 className="text-2xl font-bold mb-4">1. {t('admin_verify_ticket')}</h2>
            <p className="text-sm text-gray-600 mb-4">
              {t('admin_ticket_ref_or_phone')}
            </p>

            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-semibold mb-1">
                  {t('admin_ticket_ref_label')}
                </label>
                <input
                  type="text"
                  className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
                  placeholder={t('admin_ticket_ref_placeholder')}
                  value={bookingRefSearch}
                  onChange={(e) => setBookingRefSearch(e.target.value)}
                />
              </div>

              <div className="text-center text-gray-500 text-xs">{t('or')}</div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  {t('admin_ticket_phone_label')}
                </label>
                <input
                  type="tel"
                  className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
                  placeholder={t('admin_ticket_phone_placeholder')}
                  value={phoneSearch}
                  onChange={(e) => setPhoneSearch(e.target.value)}
                />
              </div>
            </div>

            {searchError && (
              <p className="text-sm text-red-600 mb-2">{searchError}</p>
            )}

            <button
              type="button"
              onClick={handleSearchTicket}
              disabled={searchLoading}
              className="w-full bg-primary-600 text-white font-semibold py-3 rounded-xl shadow hover:bg-primary-700 transition disabled:opacity-50"
            >
              {searchLoading ? t('admin_search_loading') : t('admin_search_button')}
            </button>

            {/* Résultats */}
            <div className="mt-6 space-y-4 max-h-80 overflow-auto">
              {foundReservations.map((res) => (
                <div
                  key={res.id}
                  className="border border-neutral-200 rounded-xl p-4 bg-neutral-50"
                >
                  <p className="font-mono text-xs mb-1">
                    {t('booking_ref')} <strong>{res.bookingRef}</strong>
                  </p>
                  <p className="text-sm mb-1">
                    {res.departure} → {res.arrival} ({res.travelDate})
                  </p>
                  <p className="text-sm mb-1">
                    <strong>{t('bus')} {res.busNumber}</strong> • {res.tour === 'matin' ? t('admin_tour_morning') : t('admin_tour_evening')}
                  </p>
                  <p className="text-sm mb-1">
                    {t('first_name')}:{' '}
                    <strong>
                      {res.passenger?.firstName} {res.passenger?.lastName}
                    </strong>{' '}
                    ({res.passenger?.phone})
                  </p>
                  <p className="text-sm mb-1">
                    {t('seat')}:{' '}
                    {Array.isArray(res.seats) ? res.seats.join(', ') : (res.assignedSeat ? `${t('seat')} ${res.assignedSeat}` : '—')}
                  </p>
                  <p className="text-sm mb-1">
                    {t('total_to_pay')}:{' '}
                    <strong>
                      {res.totalAmount?.toLocaleString()} XAF
                    </strong>
                  </p>
                  <p className="text-xs text-gray-600 mb-2">
                    {t('admin_payment_method')}:{' '}
                    {res.paymentMethod === 'airtel_money'
                      ? 'Airtel Money'
                      : res.paymentMethod === 'moov_money'
                      ? 'Moov Money'
                      : t('cash_agency')}{' '}
                    • {t('admin_status')}:{' '}
                    <span
                      className={
                        res.status === 'paid'
                          ? 'text-green-600 font-semibold'
                          : res.status === 'used'
                          ? 'text-blue-600 font-semibold'
                          : 'text-orange-600 font-semibold'
                      }
                    >
                      {res.status || 'pending'}
                    </span>
                  </p>

                  <div className="flex gap-2 text-xs">
                    <button
                      onClick={() =>
                        updateReservationStatus(res.id, 'paid')
                      }
                      className="px-3 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 transition"
                    >
                      {t('admin_mark_paid')}
                    </button>
                    <button
                      onClick={() =>
                        updateReservationStatus(res.id, 'used')
                      }
                      className="px-3 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
                    >
                      {t('admin_mark_used')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Bloc stats bus */}
          <motion.section
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white p-6 rounded-2xl shadow-lg border border-neutral-100"
          >
            <h2 className="text-2xl font-bold mb-4">2. {t('admin_bus_places')}</h2>
            <p className="text-sm text-gray-600 mb-4">
              {t('admin_select_trip')}, {t('admin_select_date')}, {t('admin_select_tour')} {t('and')} {t('admin_select_bus')} {t('to_view_reservations')}
            </p>

            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-semibold mb-1">
                  {t('admin_select_trip')}
                </label>
                <select
                  className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
                  value={selectedTripId}
                  onChange={(e) => setSelectedTripId(e.target.value)}
                >
                  <option value="">{t('admin_select_trip')}</option>
                  {routesData.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.departure} → {r.arrival}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  {t('admin_select_date')}
                </label>
                <input
                  type="date"
                  className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
                  value={dateStats}
                  onChange={(e) => setDateStats(e.target.value)}
                />
              </div>

              {/* Sélection du tour */}
              <div>
                <label className="block text-sm font-semibold mb-1">
                  {t('admin_select_tour')}
                </label>
                <select
                  className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
                  value={selectedTourStats}
                  onChange={(e) => setSelectedTourStats(e.target.value)}
                >
                  <option value="">{t('admin_select_tour')}</option>
                  <option value="matin">🌅 {t('admin_tour_morning')}</option>
                  <option value="soir">🌆 {t('admin_tour_evening')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  {t('admin_select_bus')}
                </label>
                <select
                  className="w-full p-3 rounded-xl border border-neutral-200 focus:border-primary-500 outline-none"
                  value={selectedBusStats}
                  onChange={(e) => setSelectedBusStats(e.target.value)}
                >
                  <option value="">{t('admin_select_bus')}</option>
                  <option value="1">Bus 1</option>
                  <option value="2">Bus 2</option>
                  <option value="3">Bus 3</option>
                </select>
              </div>
            </div>

            <button
              type="button"
              onClick={handleLoadStats}
              disabled={!selectedTripId || !dateStats || !selectedBusStats || !selectedTourStats || statsLoading}
              className="w-full bg-primary-600 text-white font-semibold py-3 rounded-xl shadow hover:bg-primary-700 transition disabled:opacity-50"
            >
              {statsLoading ? t('admin_loading') : t('admin_load_reservations')}
            </button>

            {selectedTrip && selectedBusStats && selectedTourStats && (
              <div className="mt-6">
                <h3 className="font-semibold mb-2">
                  {selectedTrip.departure} → {selectedTrip.arrival} ({dateStats}) - {selectedTourStats === 'matin' ? t('admin_tour_morning') : t('admin_tour_evening')} - {t('bus')} {selectedBusStats}
                </h3>
                <p className="text-sm text-gray-700 mb-1">
                  {t('admin_total_places')}: {totalSeats}
                </p>
                <p className="text-sm text-gray-700 mb-1">
                  {t('admin_reserved_places')}: {reservedSeatsCount}
                </p>
                <p className="text-sm text-gray-700 mb-3">
                  {t('admin_remaining_places')}:{' '}
                  <span
                    className={
                      remainingSeats <= 0
                        ? 'text-red-600 font-bold'
                        : 'font-bold'
                    }
                  >
                    {remainingSeats}
                  </span>
                </p>

                <div className="max-h-64 overflow-auto space-y-2 text-sm">
                  {statsReservations.map((res) => (
                    <div
                      key={res.id}
                      className="border border-neutral-200 rounded-xl p-3 bg-neutral-50"
                    >
                      <p className="font-mono text-xs mb-1">
                        {t('booking_ref')}: {res.bookingRef}
                      </p>
                      <p className="mb-1">
                        {res.passenger?.firstName} {res.passenger?.lastName} (
                        {res.passenger?.phone})
                      </p>
                      <p className="mb-1">
                        {t('seat')}:{' '}
                        {Array.isArray(res.seats) ? res.seats.join(', ') : (res.assignedSeat ? `${t('seat')} ${res.assignedSeat}` : '—')}
                      </p>
                      <p className="text-xs text-gray-600 mb-1">
                        {t('admin_payment_method')}:{' '}
                        {res.paymentMethod === 'airtel_money'
                          ? 'Airtel Money'
                          : res.paymentMethod === 'moov_money'
                          ? 'Moov Money'
                          : t('cash_agency')}{' '}
                        • {t('admin_status')}:{' '}
                        <span
                          className={
                            res.status === 'paid'
                              ? 'text-green-600 font-semibold'
                              : res.status === 'used'
                              ? 'text-blue-600 font-semibold'
                              : 'text-orange-600 font-semibold'
                          }
                        >
                          {res.status || 'pending'}
                        </span>
                      </p>
                    </div>
                  ))}

                  {statsReservations.length === 0 &&
                    !statsLoading &&
                    selectedTripId &&
                    dateStats &&
                    selectedBusStats &&
                    selectedTourStats && (
                      <p className="text-xs text-gray-500">
                        {t('admin_no_reservations')}
                      </p>
                    )}
                </div>
              </div>
            )}
          </motion.section>
        </div>
      </div>
    </div>
  );
}